// frontend/src/services/localAuthClient.js — client d'authentification du NAVIGATEUR pour l'authentification du serveur
// (REACT_APP_AUTH_BACKEND=local). Il offre la meme interface que `supabase.auth` pour la partie dont le site se sert, de sorte que
// les ~40 appels existants (getSession, getUser, onAuthStateChange, signInWithPassword, signInAnonymously, signInWithOtp,
// verifyOtp, updateUser, resetPasswordForEmail, signOut) ne changent pas. Il parle aux routes de backend/routes/authSession.js.
//
// Session : conservee dans localStorage (jeton d'acces de 1 h + jeton de renouvellement a usage unique), renouvelee en silence
// avant l'expiration, partagee entre les onglets.
//
// L'ancienne PASSERELLE (session Supabase de l'ancien site echangee contre une session du serveur) a ete retiree le 2026-10-23,
// avec Supabase lui-meme : un visiteur d'avant le 2026-10-18 se reconnecte simplement par e-mail (meme compte, memes livres).

const CLE_SESSION = 'bookipix.auth.session';
const MARGE_RENOUVELLEMENT_S = 120;

function erreurAuth(message, status = 400, code = undefined) {
  const e = new Error(message);
  e.name = 'AuthApiError';
  e.status = status;
  e.code = code;
  e.__isAuthError = true;
  return e;
}

export function createLocalAuthClient({ apiBaseUrl, stockage = null, fetchImpl = null, maintenant = () => Date.now() } = {}) {
  const base = String(apiBaseUrl || '/api').replace(/\/$/, '');
  const appel = (...args) => (fetchImpl || fetch)(...args);
  let memoire = null; // repli si localStorage est indisponible (navigation privee stricte)
  const magasin = stockage || {
    get: () => { try { return window.localStorage.getItem(CLE_SESSION); } catch (_e) { return memoire; } },
    set: (v) => { memoire = v; try { window.localStorage.setItem(CLE_SESSION, v); } catch (_e) { /* tant pis : session de l'onglet */ } },
    del: () => { memoire = null; try { window.localStorage.removeItem(CLE_SESSION); } catch (_e) { /* idem */ } }
  };
  const abonnes = new Set();
  let minuterie = null;
  let renouvellementEnCours = null;

  const lire = () => {
    try {
      const brut = magasin.get();
      const s = brut ? JSON.parse(brut) : null;
      return s && s.access_token && s.refresh_token ? s : null;
    } catch (_e) { return null; }
  };
  const emettre = (evenement, session) => {
    abonnes.forEach((cb) => { try { cb(evenement, session); } catch (_e) { /* un ecouteur fautif ne casse pas les autres */ } });
  };
  const planifier = (session) => {
    if (minuterie) clearTimeout(minuterie);
    minuterie = null;
    if (!session || typeof window === 'undefined') return;
    const dans = Math.max((session.expires_at - MARGE_RENOUVELLEMENT_S) * 1000 - maintenant(), 5000);
    minuterie = setTimeout(() => { renouveler().catch(() => {}); }, Math.min(dans, 2147483000));
    if (minuterie && minuterie.unref) minuterie.unref();
  };
  const enregistrer = (session, evenement) => {
    magasin.set(JSON.stringify(session));
    planifier(session);
    if (evenement) emettre(evenement, session);
    return session;
  };
  const oublier = (evenement = 'SIGNED_OUT') => {
    magasin.del();
    planifier(null);
    if (evenement) emettre(evenement, null);
  };

  async function requete(chemin, { methode = 'POST', corps, jeton } = {}) {
    let reponse;
    try {
      reponse = await appel(`${base}/auth${chemin}`, {
        method: methode,
        headers: { 'Content-Type': 'application/json', ...(jeton ? { Authorization: `Bearer ${jeton}` } : {}) },
        body: corps === undefined ? undefined : JSON.stringify(corps)
      });
    } catch (_e) {
      return { donnees: null, erreur: erreurAuth('Failed to fetch', 0, 'network_error') };
    }
    const donnees = await reponse.json().catch(() => ({}));
    if (!reponse.ok) return { donnees: null, erreur: erreurAuth(donnees.error || 'Erreur', reponse.status, donnees.code) };
    return { donnees, erreur: null };
  }

  async function renouveler() {
    if (renouvellementEnCours) return renouvellementEnCours;
    renouvellementEnCours = (async () => {
      const actuelle = lire();
      if (!actuelle) return null;
      const { donnees, erreur } = await requete('/refresh', { corps: { refresh_token: actuelle.refresh_token } });
      if (erreur) {
        // Refus net du serveur : la session est morte. Panne reseau : on la garde, on reessaiera.
        if (erreur.status >= 400 && erreur.status < 500 && erreur.status !== 429) oublier('SIGNED_OUT');
        return null;
      }
      return enregistrer(donnees, 'TOKEN_REFRESHED');
    })().finally(() => { renouvellementEnCours = null; });
    return renouvellementEnCours;
  }

  // Session deja presente au chargement : son renouvellement est planifie tout de suite.
  if (typeof window !== 'undefined') planifier(lire());
  const pretePromesse = Promise.resolve();

  async function sessionValide() {
    await pretePromesse;
    let s = lire();
    if (!s) return null;
    if (s.expires_at - MARGE_RENOUVELLEMENT_S <= Math.floor(maintenant() / 1000)) {
      const neuve = await renouveler();
      if (neuve) return neuve;
      // Renouvellement refuse : la session a ete oubliee (relue vide). Panne reseau : on garde l'ancienne tant qu'elle est valable.
      s = lire();
      if (s && s.expires_at <= Math.floor(maintenant() / 1000)) s = null;
    }
    return s;
  }

  if (typeof window !== 'undefined') {
    // Autre onglet : connexion, deconnexion ou renouvellement.
    window.addEventListener('storage', (e) => {
      if (e.key !== CLE_SESSION) return;
      const s = lire();
      planifier(s);
      emettre(s ? 'SIGNED_IN' : 'SIGNED_OUT', s);
    });
    // Retour sur l'onglet apres une longue veille : le minuteur a pu etre gele.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && lire()) sessionValide().catch(() => {});
    });
  }

  const reponseSession = (donnees, erreur, evenement = 'SIGNED_IN') => {
    if (erreur) return { data: { user: null, session: null }, error: erreur };
    enregistrer(donnees, evenement);
    return { data: { user: donnees.user, session: donnees }, error: null };
  };

  return {
    async getSession() {
      return { data: { session: await sessionValide() }, error: null };
    },

    async getUser() {
      const s = await sessionValide();
      if (!s) return { data: { user: null }, error: erreurAuth('Auth session missing!', 400, 'session_missing') };
      let r = await requete('/user', { methode: 'GET', jeton: s.access_token });
      if (r.erreur && r.erreur.status === 401) {
        const neuve = await renouveler();
        if (neuve) r = await requete('/user', { methode: 'GET', jeton: neuve.access_token });
      }
      if (r.erreur) return { data: { user: null }, error: r.erreur };
      return { data: { user: r.donnees.user }, error: null };
    },

    onAuthStateChange(rappel) {
      abonnes.add(rappel);
      // Comme supabase-js : l'etat courant est annonce juste apres l'abonnement.
      sessionValide().then((s) => { if (abonnes.has(rappel)) { try { rappel('INITIAL_SESSION', s); } catch (_e) { /* idem */ } } }).catch(() => {});
      return { data: { subscription: { unsubscribe: () => abonnes.delete(rappel) } } };
    },

    async signInWithPassword({ email, password }) {
      const { donnees, erreur } = await requete('/token', { corps: { email, password } });
      return reponseSession(donnees, erreur);
    },

    async signUp({ email, password, options = {} }) {
      const { donnees, erreur } = await requete('/signup', { corps: { email, password, data: options.data } });
      return reponseSession(donnees, erreur);
    },

    async signInAnonymously() {
      const { donnees, erreur } = await requete('/signin-anonymous', { corps: {} });
      return reponseSession(donnees, erreur);
    },

    async signInWithOtp({ email, options = {} }) {
      const { erreur } = await requete('/otp', { corps: { email, type: 'login', shouldCreateUser: options.shouldCreateUser !== false, lang: (typeof navigator !== 'undefined' && navigator.language) || 'fr' } });
      return { data: { user: null, session: null }, error: erreur };
    },

    async verifyOtp({ email, token, type = 'email' }) {
      const { donnees, erreur } = await requete('/verify', { corps: { email, token, type } });
      return reponseSession(donnees, erreur, type === 'recovery' ? 'PASSWORD_RECOVERY' : 'SIGNED_IN');
    },

    // Mot de passe oublie : un CODE (comme la connexion), plus un lien. La page de reinitialisation le recoit ensuite.
    async resetPasswordForEmail(email) {
      const { erreur } = await requete('/otp', { corps: { email, type: 'recovery', lang: (typeof navigator !== 'undefined' && navigator.language) || 'fr' } });
      return { data: {}, error: erreur };
    },

    async updateUser(attrs = {}) {
      const s = await sessionValide();
      if (!s) return { data: { user: null }, error: erreurAuth('Auth session missing!', 400, 'session_missing') };
      let r = await requete('/user', { methode: 'PUT', jeton: s.access_token, corps: attrs });
      if (r.erreur && r.erreur.status === 401) {
        const neuve = await renouveler();
        if (neuve) r = await requete('/user', { methode: 'PUT', jeton: neuve.access_token, corps: attrs });
      }
      if (r.erreur) return { data: { user: null }, error: r.erreur };
      const courante = lire();
      if (courante) enregistrer({ ...courante, user: r.donnees.user }, 'USER_UPDATED');
      return { data: { user: r.donnees.user }, error: null };
    },

    async signOut() {
      const s = lire();
      oublier('SIGNED_OUT'); // d'abord : la deconnexion est immediate pour l'utilisateur, meme hors ligne
      if (s) await requete('/signout', { corps: { refresh_token: s.refresh_token } });
      return { error: null };
    },

    __attendrePasserelle: () => pretePromesse
  };
}
